import { EditOutlined, FileTextOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Input from '@/components/Inputs/Input';
import Tooltip from '@/components/Tooltip/TooltipV2';
import Heading from '@/components/Typography/Heading';
import { usePublic } from '@/contexts/PublicContext';
import { updateReportMutationOptions } from '@/entities/report';
import { SharedLinkDialog } from '@/entities/shared-link/ui/shared-link-dialog';
import { ReportPublicDocumentHeader } from '@/features/(reports)/report';
import useColor from '@/hooks/domain/useColor';
import useWorkspaceId from '@/hooks/domain/useWorkspaceId';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import useModalState from '@/hooks/pure/useModalState';
import type { Schema } from '@/shared/api/generated';
import { Breadcrumb } from '@/shared/ui';
import { useToast } from '@/stores/pure/ToastStore';
import { getTimezoneDayjs } from '@/utils/time';
import { isEmptyString } from '@/utils/util';

import useReportStore from '../../store/ReportStore';
import type { ReportT } from '../../types';

type Props = {
  report: ReportT;
};

const ReportDetailHeader = (props: Props) => {
  const isPublic = usePublic();
  const color = useColor();
  const addToast = useToast();
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();
  const { report } = props;
  const wr = useWorkspaceRouter();
  const mode = useReportStore((state) => state.mode);
  const setReportTitle = useReportStore((state) => state.setReportTitle);
  const setMode = useReportStore((state) => state.setMode);
  const backupReport = useReportStore((state) => state.backupReport);
  const reportBackup = useReportStore((state) => state.reportBackup);
  const restoreReport = useReportStore((state) => state.restoreReport);
  const clearBackup = useReportStore((state) => state.clearBackup);
  const shareModalState = useModalState();
  const [isSaving, setIsSaving] = useState(false);
  const [title, setTitle] = useState(report.title);
  useEffect(() => {
    setTitle(report.title);
  }, [report.title]);
  const targetLink = `${window.location.origin}/workspace/${workspaceId}/report/${report.alias}`;
  const updateReportMutation = useMutation(
    updateReportMutationOptions(queryClient),
  );

  const isReportEditMode = mode === 'EDIT';

  const onEditClick = () => {
    backupReport();
    setMode('EDIT');
  };

  const onCancelClick = () => {
    setTitle(reportBackup?.title || '');
    restoreReport();
    setMode('DETAIL');
  };

  const onSaveClick = async () => {
    try {
      setIsSaving(true);
      await updateReportMutation.mutateAsync({
        report_id: report.alias,
        title: report.title,
        items: report.items as Schema<'UpdateReportRequest'>['items'],
        rows: report.rows as Schema<'UpdateReportRequest'>['rows'],
      });
      addToast('Report updated', 'success');
      clearBackup();
    } catch (e) {
      console.error(e);
      setTitle(reportBackup?.title || '');
      addToast('Failed to update report', 'fail');
      restoreReport();
    } finally {
      setIsSaving(false);
      setMode('DETAIL');
    }
  };

  if (isPublic) {
    return (
      <ReportPublicDocumentHeader
        title={report.title}
        date={`Edited ${getTimezoneDayjs(report.updated_at).fromNow()}`}
      />
    );
  }

  if (isReportEditMode) {
    return (
      <div>
        <Flex g={16} d="row" padding="34px 40px" j="space-between" a="center">
          <FileTextOutlined style={{ fontSize: 20 }} />
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => {
              if (isEmptyString(title)) {
                setTitle(report.title);
              } else {
                setReportTitle(title);
              }
            }}
            sizeType="l"
            placeholder="Type report title"
            style={{
              width: '100%',
              maxWidth: 600,
              fontSize: 24,
              fontWeight: 600,
              height: 40,
              color: color.gray700,
            }}
          />
          <Flex g={8}>
            <Button
              variant="secondary"
              sizeType="m"
              onClick={onCancelClick}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              sizeType="m"
              onClick={onSaveClick}
              isLoading={isSaving}
            >
              Save
            </Button>
          </Flex>
        </Flex>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center gap-4 px-8 py-5">
      <VFlex g={16} f={1}>
        <div className="flex items-center min-w-0 gap-2 text-sm font-medium shrink-0">
          <Breadcrumb
            label="Reports"
            onClick={() => {
              void wr.push('/reports');
            }}
            ariaLabel="Go to reports"
          />
          <span className="text-border">/</span>
          <Breadcrumb label={report.title} />
        </div>
        <Flex j="space-between" a="center">
          <Flex a="center">
            <FileTextOutlined style={{ fontSize: 20 }} />
            <Space w={12} />
            <Tooltip tooltipNode={report.title}>
              <Heading
                type="h2"
                multilineEllipsis={1}
                style={{ maxWidth: 800 }}
              >
                {report.title}
              </Heading>
            </Tooltip>
          </Flex>
          <Flex g={8}>
            <Button
              variant="secondary"
              sizeType="m"
              leftIcon={<EditOutlined />}
              onClick={onEditClick}
            >
              Edit
            </Button>
            <Button
              variant="primary"
              sizeType="m"
              onClick={() => shareModalState.open()}
            >
              Share
            </Button>
          </Flex>
        </Flex>
      </VFlex>

      <SharedLinkDialog
        title="Share report"
        description="Manage the shared link settings for this report."
        targetId={report.alias}
        targetType="REPORT"
        targetLink={targetLink}
        isOpen={shareModalState.status}
        close={() => {
          void shareModalState.close();
        }}
      />
    </div>
  );
};

export default ReportDetailHeader;
